import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { pmFieldClass, pmLabelClass } from '../port-management.ui';

interface PortFormProps {
    initialData?: any;
    onSuccess: () => void;
    onCancel: () => void;
}

export function PortForm({ initialData, onSuccess, onCancel }: PortFormProps) {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        defaultValues: initialData || {
            name: '',
            code: '',
            country: '',
            state: '',
            city: '',
            type: 'Seaport',
            status: 'Active',
            latitude: '',
            longitude: '',
        },
    });
    const toast = useToast();

    const mutation = useMutation({
        mutationFn: async (data: any) => {
            const url = initialData ? `/api/v1/ports/${initialData._id}` : '/api/v1/ports';
            const method = initialData ? 'PUT' : 'POST';
            const res = await authenticatedFetch(url, {
                method,
                body: JSON.stringify(data),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save port');
            }
            return res.json();
        },
        onSuccess: () => {
            toast.success(initialData ? 'Port updated' : 'Port created');
            onSuccess();
        },
        onError: (err: any) => {
            toast.error(err.message);
        },
    });

    const onSubmit = (data: any) => {
        mutation.mutate(data);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Port Name *</label>
                    <input
                        {...register('name', { required: 'Name is required' })}
                        className={pmFieldClass}
                    />
                    {errors.name && <span className="text-red-500 text-xs">{errors.name.message as string}</span>}
                </div>
                <div>
                    <label className={pmLabelClass}>UN/LOCODE *</label>
                    <input
                        {...register('code', { required: 'Code is required', minLength: 5, maxLength: 5 })}
                        className={pmFieldClass}
                        placeholder="e.g. INMAA"
                    />
                    {errors.code && <span className="text-red-500 text-xs">{errors.code.message as string}</span>}
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div>
                    <label className={pmLabelClass}>Country *</label>
                    <input
                        {...register('country', { required: 'Country is required' })}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>State</label>
                    <input
                        {...register('state')}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>City</label>
                    <input
                        {...register('city')}
                        className={pmFieldClass}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Type</label>
                    <select
                        {...register('type')}
                        className={pmFieldClass}
                    >
                        <option value="Seaport">Seaport</option>
                        <option value="Inland">Inland</option>
                        <option value="Anchorage">Anchorage</option>
                    </select>
                </div>
                <div>
                    <label className={pmLabelClass}>Status</label>
                    <select
                        {...register('status')}
                        className={pmFieldClass}
                    >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Latitude</label>
                    <input
                        type="number"
                        step="any"
                        {...register('latitude')}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>Longitude</label>
                    <input
                        type="number"
                        step="any"
                        {...register('longitude')}
                        className={pmFieldClass}
                    />
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>
                    Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className={erpPageBtnPrimaryClass}>
                    {isSubmitting ? 'Saving...' : 'Save port'}
                </button>
            </div>
        </form>
    );
}
